import { DEFAULT_GUARDRAILS, WEEKDAYS, type Audience, type BrandProfile, type Pillar } from './brand';

/**
 * A new brand: the industry template's audiences and pillars and a conservative cadence on three
 * social channels (fits every plan). Nothing is planned until someone presses Plan.
 */
export function starterBrandProfile(input: { name: string; timezone: string; audiences: Audience[]; pillars: Pillar[] }): BrandProfile {
  return {
    name: input.name,
    website: null,
    description: '',
    voice: '',
    timezone: input.timezone,
    languages: ['en'],
    ctaUrl: null,
    mailingAddress: '',
    audiences: input.audiences,
    pillars: input.pillars,
    guardrails: DEFAULT_GUARDRAILS,
    cadence: [
      { channel: 'linkedin', postsPerWeek: 3, times: ['09:30', '12:15'], days: WEEKDAYS },
      { channel: 'instagram', postsPerWeek: 3, times: ['18:00'], days: [1, 2, 3, 4, 5, 6, 7] },
      { channel: 'facebook', postsPerWeek: 2, times: ['12:00'], days: WEEKDAYS },
    ],
  };
}

/**
 * Konekto, MarketingBee's first customer (and WRKRBZ's publishing partner). Taken from Konekto's own
 * growth brief (BacKendKonekto/docs/konekto-automation-plan.md): two-sided marketplace, beauty and
 * wellness first, the founder pillar stays human-written, no invented social proof.
 */
export const KONEKTO_BRAND: BrandProfile = {
  name: 'Konekto',
  website: 'https://konekto.io',
  description:
    'Konekto is the shared workspace where brands, agencies and creators run an influencer campaign from first brief to final payment: discovery, applications, chat, contracts, content approval and payouts in one record. Free to start, always free for creators.',
  voice: 'Calm, editorial and specific. Names the real pain (spreadsheets, DMs, chasing payments) and offers relief. Never hype, never exclamation-heavy.',
  timezone: 'America/Toronto',
  languages: ['en'],
  ctaUrl: 'https://konekto.io',
  // Konekto's real business address must be filled in before the first email goes out.
  mailingAddress: '',
  audiences: [
    {
      id: 'beauty-brands',
      name: 'DTC beauty and wellness brands',
      description: 'DTC e-commerce, $2M-$20M revenue, 5-40 staff, a marketing team of 1-3 in skincare, cosmetics, supplements or self-care.',
      pains: ['Creator collabs managed across Gmail, a spreadsheet, PayPal and group chats', 'No single record of who agreed to what, for how much', 'Approving content in DM threads'],
    },
    {
      id: 'beauty-creators',
      name: 'Beauty and wellness micro creators',
      description: 'Nano and micro creators with 1K-50K followers in beauty, skincare and wellness.',
      pains: ['Not knowing what rate is fair', 'Brands that pay late or not at all', 'Briefs that change after the content is shot'],
    },
    {
      id: 'agencies',
      name: 'Boutique influencer agencies',
      description: 'Small agencies managing a creator roster for several beauty and wellness clients.',
      pains: ['Reporting to clients from screenshots', 'Contracts and payouts scattered per client'],
    },
  ],
  pillars: [
    { id: 'tool-chaos', name: 'Tool chaos', description: 'The fragmented-tools pain on the brand side: one campaign, six tools, nothing in one place.', weight: 3, humanOnly: false },
    { id: 'payment-trust', name: 'Payment trust', description: 'Creators getting paid on time, with the terms written down before the work starts.', weight: 3, humanOnly: false },
    { id: 'playbooks', name: 'Operator playbooks', description: 'Practical templates: a brief template, a rate-card sanity check, a content approval checklist.', weight: 2, humanOnly: false },
    { id: 'building-in-public', name: 'Building in public', description: 'The founder, in their own words: why Konekto exists and what was learned this week.', weight: 1, humanOnly: true },
  ],
  guardrails: {
    ...DEFAULT_GUARDRAILS,
    bannedPhrases: ['game-changer', 'revolutionize', 'disrupt', 'unlock your potential', 'skyrocket'],
    avoidTopics: ['naming or criticising competitors', 'income promises to creators', 'follower-count bragging'],
    approvedClaims: ['Free to start', 'No credit card', 'Always free for creators'],
  },
  cadence: [
    { channel: 'linkedin', postsPerWeek: 3, times: ['09:30', '12:15'], days: WEEKDAYS },
    { channel: 'instagram', postsPerWeek: 4, times: ['18:00', '12:30'], days: [1, 2, 3, 4, 5, 6, 7] },
    { channel: 'facebook', postsPerWeek: 2, times: ['12:00'], days: WEEKDAYS },
    { channel: 'bluesky', postsPerWeek: 3, times: ['10:00', '16:00'], days: WEEKDAYS },
    { channel: 'email', postsPerWeek: 1, times: ['10:00'], days: [2] },
    { channel: 'blog', postsPerWeek: 1, times: ['08:00'], days: [4] },
  ],
};
